import { useCallback, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { ArchivedFile } from '../../DownloadUrlsModalButton.types';

import { RESTORE_S3_OBJECTS_QUERY } from '../../../../../../fileAttachments/queries/restoreS3Objects.query';

import { useRestoreS3Objects } from '../../../../../../fileAttachments/hooks/useRestoreS3Objects';
import { useTableCheckable } from '../../../../../../../common/hooks/useTableCheckable';
import { useToastNotification } from '../../../../../../../common/hooks/useToastNotification';

import { ArchivedFilesListItem } from './components/ArchivedFilesListItem';

import { AlertMessage } from '../../../../../../../helpers/AlertMessage';
import { Checkbox } from '../../../../../../../helpers/Checkbox';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { SimpleModalButtons } from '../../../../../../../helpers/modals/SimpleModal/components/SimpleModalButtons';
import { Translate } from '../../../../../../../helpers/Translate';

import { FileAttachmentsPermissions } from '../../../../../../fileAttachments/fileAttachmentsConstants';

import {
  sourceFilesKeys,
  stringsKeys,
  words
} from '../../../../../../../locales/keys';

interface ArchivedFilesListProps {
  archivedFiles: ArchivedFile[];
  hideModal: () => void;
}

function ArchivedFilesList({
  archivedFiles,
  hideModal
}: ArchivedFilesListProps) {
  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const [selectableFilesHash, setSelectableFilesHash] = useState<
    Record<ArchivedFile['id'], boolean>
  >({});

  const handleSelectableChange = useCallback(
    (fileId: ArchivedFile['id'], selectable: boolean) => {
      setSelectableFilesHash((previous) => {
        if (!!previous[fileId] === selectable) {
          return previous;
        }

        return { ...previous, [fileId]: selectable };
      });
    },
    []
  );

  const selectableFiles = archivedFiles.filter(
    (archivedFile) => selectableFilesHash[archivedFile.id]
  );

  const {
    checkedHash,
    checkedItems,
    checkedAll,
    handleSetCheckedIds,
    handleCheckAll
  } = useTableCheckable({ items: selectableFiles });

  const {
    restoreS3Objects,
    restoreS3ObjectsErrorMessage,
    restoreS3ObjectsLoading
  } = useRestoreS3Objects({
    query: RESTORE_S3_OBJECTS_QUERY
  });

  const { showToastI18nNotification } = useToastNotification({
    appearance: 'success',
    i18nMessage:
      stringsKeys.selectedFilesHaveBeenSuccessfullyScheduledForRestoration
  });

  const handleRestoreS3Objects = useCallback(async () => {
    if (isEmpty(checkedItems) || restoreS3ObjectsLoading) {
      return;
    }

    try {
      await restoreS3Objects({
        data: map(checkedItems, (archivedFile) => ({
          uuid: archivedFile.uuid,
          className: archivedFile.className,
          days: archivedFile.className === 'source_file' ? 1 : 60,
          tier: 'Standard'
        }))
      });

      showToastI18nNotification();
      hideModal();
    } catch (e) {
      console.error(e);
    }
  }, [
    checkedItems,
    hideModal,
    restoreS3Objects,
    restoreS3ObjectsLoading,
    showToastI18nNotification
  ]);

  return (
    <>
      <div className="flex-1 overflow-y-auto px-2 z-0">
        <div className="py-4 sm:px-2 flex flex-col gap-y-6 relative">
          <div className="p-2">
            <Translate
              id={
                stringsKeys.theseFilesAreStoredInDeepArchiveBeforeDownloadingTheyMustFirstBeRestoredRestorationTypicallyTakesUpTo
              }
            />
            <span className="font-bold text-blue-500">
              12 <Translate id={words.timer.hours} />
            </span>
            <Translate
              id={stringsKeys.onceRestoredTheFilesWillBeAvailableForDownloadFor}
            />
            <span className="font-bold text-blue-500">
              60 <Translate id={words.timer.days} />
            </span>

            <CheckPermissions
              action={
                FileAttachmentsPermissions.READ_RESTORE_S3_OBJECTS_MODAL_SOURCE_FILES_ACCESSIBILITY_TEXT
              }
            >
              &nbsp;(
              <span className="font-bold">
                <Translate id={sourceFilesKeys.pluralLowerCase} />
              </span>
              <Translate id={stringsKeys.remainAccessibleFor} />
              <span className="font-bold text-blue-500">
                24 <Translate id={words.timer.hours} />
              </span>
              )
            </CheckPermissions>

            <Translate
              id={
                stringsKeys.afterThisPeriodTheyWillReturnToDeepArchiveAndMustBeRestoredAgainToAccessThem
              }
            />
          </div>

          <div className="p-2 flex flex-col gap-y-6">
            <div className="flex items-center">
              <div className="flex items-center">
                <Checkbox
                  checked={checkedAll}
                  disabled={isEmpty(selectableFiles) || restoreS3ObjectsLoading}
                  id="check-all-archived-files"
                  onChange={handleCheckAll}
                />
              </div>
              <div className="pl-3 pr-1 font-medium text-black dark:text-white">
                <Translate id={stringsKeys.allFiles} />
              </div>
            </div>

            <div>
              {archivedFiles?.map((archivedFile) => (
                <ArchivedFilesListItem
                  archivedFile={archivedFile}
                  checked={checkedHash?.[archivedFile.id]}
                  disabled={restoreS3ObjectsLoading}
                  key={archivedFile.id}
                  onCheck={handleSetCheckedIds}
                  onSelectableChange={handleSelectableChange}
                />
              ))}
            </div>

            <AlertMessage message={restoreS3ObjectsErrorMessage} />
          </div>
        </div>
      </div>

      <SimpleModalButtons
        disabled={restoreS3ObjectsLoading}
        handleSubmit={handleRestoreS3Objects}
        hideModal={hideModal}
        i18nFooterText={stringsKeys.restoreSelectedFiles}
        i18nSubmitText={words.restore}
        isLoading={restoreS3ObjectsLoading}
        ref={submitButtonRef}
        submitDisabled={isEmpty(checkedItems) || restoreS3ObjectsLoading}
      />
    </>
  );
}

export default ArchivedFilesList;
